import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Unsplash images — rehearsal spaces & studio musicians
const gridImages = [
  { src: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80', alt: 'Recording studio session', span: 'large' },
  { src: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=600&q=80', alt: 'Musicians performing', span: 'small' },
  { src: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=600&q=80', alt: 'Guitar player', span: 'small' },
  { src: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&q=80', alt: 'Band rehearsal', span: 'small' },
  { src: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80', alt: 'Drummer rehearsing', span: 'small' },
  { src: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600&q=80', alt: 'Music studio', span: 'small' },
];

const VenuePromoSection = () => {
  const navigate = useNavigate();

  return (
    <section style={{
      background: '#0f172a',
      padding: '20px 20px',
      overflow: 'hidden',
    }}>
      <style>{`
        @media (max-width: 768px) {
          .venue-promo-flex { gap: 32px !important; }
        }
      `}</style>
      <div className="venue-promo-flex" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '60px',
      }}>
        {/* ── Left: text ── */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ flex: '1 1 320px' }}
        >
          <h2 style={{
            fontFamily: 'Gilroy-semiBold, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)',
            color: '#fff',
            lineHeight: 1.2,
            marginBottom: '20px',
            letterSpacing: '-0.02em',
          }}>
            Own a rehearsal space?
          </h2>

          <p style={{
            color: '#94a3b8',
            fontSize: '1.05rem',
            lineHeight: 1.8,
            fontFamily: 'Poppins, sans-serif',
            marginBottom: '12px',
          }}>
            Reach new musicians in your area and fill empty slots at no extra cost.
          </p>
          <p style={{
            color: '#94a3b8',
            fontSize: '1.05rem',
            lineHeight: 1.8,
            fontFamily: 'Poppins, sans-serif',
            marginBottom: '36px',
          }}>
            List your rooms and manage bookings on our dedicated platform for rehearsal spaces.
          </p>

          <button
            onClick={() => navigate('/login?signup=1&venue=1')}
            style={{
              background: '#374EA9',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              padding: '14px 32px',
              fontSize: '1rem',
              fontFamily: 'Gilroy-semiBold, sans-serif',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(55,78,169,0.4)',
              transition: 'background 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#4a62d6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#374EA9'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Register your space →
          </button>
        </motion.div>

        {/* ── Right: photo mosaic ── */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={{ flex: '1 1 420px' }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: '200px 200px',
            gap: '8px',
            borderRadius: '16px',
            overflow: 'hidden',
          }}>
            {/* Large left image spanning both rows */}
            <div style={{ gridColumn: '1 / 2', gridRow: '1 / 3', overflow: 'hidden' }}>
              <img
                src={gridImages[0].src}
                alt={gridImages[0].alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            </div>
            {/* Top-center */}
            <div style={{ gridColumn: '2 / 3', gridRow: '1 / 2', overflow: 'hidden' }}>
              <img
                src={gridImages[1].src}
                alt={gridImages[1].alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            </div>
            {/* Top-right */}
            <div style={{ gridColumn: '3 / 4', gridRow: '1 / 2', overflow: 'hidden' }}>
              <img
                src={gridImages[2].src}
                alt={gridImages[2].alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            </div>
            {/* Bottom-center */}
            <div style={{ gridColumn: '2 / 3', gridRow: '2 / 3', overflow: 'hidden' }}>
              <img
                src={gridImages[3].src}
                alt={gridImages[3].alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            </div>
            {/* Bottom-right */}
            <div style={{ gridColumn: '3 / 4', gridRow: '2 / 3', overflow: 'hidden' }}>
              <img
                src={gridImages[4].src}
                alt={gridImages[4].alt}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VenuePromoSection;
